import {
  DatepickerV2,
  DD,
  DT,
  FormField,
  GridCol,
  GridRow,
  Input,
  isDefinedNumber,
  isDefinedString,
  isValidDefinedDate,
  Select,
  toUTCDate,
  toZonedDateStringOrUndefined,
  Typography,
} from '@client/toolkit';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';

import dayjs, { Dayjs } from 'dayjs';
import { FormValues, FormPath } from '../agreement-conditions-card.component';
import { useMemo, useState, useEffect, useCallback } from 'react';

const YEAR_PERIOD_OPTIONS = [3, 6] as const;
const DEFAULT_PERIOD = 3;
const PERIOD_TOLERANCE_DAYS = 1;
const YEAR_DIFF_TOLERANCE = 0.003;

type PeriodOption = (typeof YEAR_PERIOD_OPTIONS)[number] | '';

const calculatePeriodFromDates = (startDate: Date | undefined, endDate: Date | undefined): PeriodOption => {
  if (!startDate || !endDate) {
    return DEFAULT_PERIOD;
  }

  const start = dayjs(startDate);
  const end = dayjs(endDate);

  if (!start.isValid() || !end.isValid()) {
    return DEFAULT_PERIOD;
  }

  const yearsDiff = end.diff(start, 'year', true);

  if (Math.abs(yearsDiff - 3) < YEAR_DIFF_TOLERANCE) {
    return 3;
  }
  if (Math.abs(yearsDiff - 6) < YEAR_DIFF_TOLERANCE) {
    return 6;
  }

  return '';
};

const calculateEndDateFromPeriod = (startDate: Dayjs, period: number): string => {
  // End date is inclusive ("Til og med"), so subtract 1 day from the exclusive end
  // Example: Start 01.01.2026 + 3 years = 01.01.2029, then -1 day = 31.12.2028
  return startDate.add(period, 'year').subtract(1, 'day').format('YYYY-MM-DD');
};

const isEndDateMatchingPeriod = (startDate: Dayjs, endDate: Dayjs, period: number): boolean => {
  // End date is inclusive ("Til og med"), so expected end is 1 day before the exclusive end
  const expectedEndDate = startDate.add(period, 'year').subtract(1, 'day');
  return Math.abs(endDate.diff(expectedEndDate, 'day')) <= PERIOD_TOLERANCE_DAYS;
};

export const RentalPeriod = ({
  isFetching,
  startDate,
  endDate,
  editing,
  optionLength,
  notificationDeadlineMonths,
  terminationNoticePeriodMonths,
}: {
  isFetching: boolean;
  startDate?: string;
  endDate?: string;
  editing: boolean;
  optionLength?: { type?: string | null; count?: number | null } | null;
  notificationDeadlineMonths?: number | null;
  terminationNoticePeriodMonths?: number | null;
}) => {
  const { t: tGlobal } = useTranslation('global');
  const { t: tCommon } = useTranslation('common');
  const { setValue, watch } = useFormContext<FormValues>();

  const startDateMemo = useMemo(() => (isDefinedString(startDate) ? toUTCDate(startDate) : undefined), [startDate]);
  const endDateMemo = useMemo(() => (isDefinedString(endDate) ? toUTCDate(endDate) : undefined), [endDate]);

  const calculatedPeriod = useMemo(
    () => calculatePeriodFromDates(startDateMemo, endDateMemo),
    [startDateMemo, endDateMemo],
  );

  const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption>(calculatedPeriod);

  const watchedStartDate = watch('duration.start');
  const watchedEndDate = watch('duration.end');

  const updateEndDate = useCallback(
    (startDateValue: string, period: number) => {
      const start = dayjs(startDateValue);
      if (!start.isValid()) return;

      const calculatedEndDate = calculateEndDateFromPeriod(start, period);
      setValue<FormPath>('duration.end', calculatedEndDate as any, {
        shouldDirty: true,
        shouldTouch: false,
      });
    },
    [setValue],
  );

  // Sync period with calculated period from props
  useEffect(() => {
    setSelectedPeriod(calculatedPeriod);
  }, [calculatedPeriod, editing]);

  // Calculate end date when period or start date changes in edit mode
  useEffect(() => {
    if (!editing || !selectedPeriod || !watchedStartDate) return;
    updateEndDate(watchedStartDate, selectedPeriod);
  }, [selectedPeriod, watchedStartDate, editing, updateEndDate]);

  // Clear period when end date is manually changed in edit mode
  useEffect(() => {
    if (!editing || !watchedStartDate || !watchedEndDate || !selectedPeriod) return;

    const start = dayjs(watchedStartDate);
    const end = dayjs(watchedEndDate);
    if (!start.isValid() || !end.isValid()) return;

    if (!isEndDateMatchingPeriod(start, end, selectedPeriod)) {
      setSelectedPeriod('');
    }
  }, [watchedEndDate, watchedStartDate, selectedPeriod, editing]);

  const handlePeriodChange = useCallback(
    (value: string) => {
      const period: PeriodOption = value === '' ? '' : (Number(value) as PeriodOption);
      setSelectedPeriod(period);

      if (period && watchedStartDate) {
        updateEndDate(watchedStartDate, period);
      }
    },
    [watchedStartDate, updateEndDate],
  );

  const handleStartDateChange = useCallback(
    (date: Dayjs | null, onChange: (value: string) => void) => {
      const dateString = date?.format('YYYY-MM-DD') || '';
      onChange(dateString);

      if (selectedPeriod && date?.isValid()) {
        const calculatedEndDate = calculateEndDateFromPeriod(date, selectedPeriod);
        setValue<FormPath>('duration.end', calculatedEndDate as any, {
          shouldDirty: true,
          shouldTouch: false,
        });
      }
    },
    [selectedPeriod, setValue],
  );

  const handleEndDateChange = useCallback(
    (date: Dayjs | null, onChange: (value: string) => void) => {
      const dateString = date?.format('YYYY-MM-DD') || '';
      onChange(dateString);

      if (selectedPeriod) {
        setSelectedPeriod('');
      }
    },
    [selectedPeriod],
  );

  return (
    <div className="py-2" data-testid="rental-period">
      <Typography variant="none" className="mb-4 text-brand-primary font-medium uppercase text-xs">
        {tGlobal('agreement.duration')}
      </Typography>

      <GridRow cols={3} spacingY={1} spacingX={2} className="items-center">
        <GridCol span={1}>
          <DT>{tGlobal('agreement.period')}</DT>
        </GridCol>
        <GridCol span={2} loading={isFetching}>
          {!editing ? (
            <DD isEmpty={!selectedPeriod} emptyValue={tCommon('custom')}>{`${selectedPeriod} ${tCommon('year')}`}</DD>
          ) : (
            <Select size="medium" value={selectedPeriod ?? ''} onChange={(e) => handlePeriodChange(e.target.value)}>
              <option value="">{tCommon('custom')}</option>
              {YEAR_PERIOD_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option} {tCommon('year')}
                </option>
              ))}
            </Select>
          )}
        </GridCol>
        <GridCol span={1}>
          <DT>{tGlobal('agreement.durationStart')}</DT>
        </GridCol>
        <GridCol span={2} loading={isFetching}>
          {!editing ? (
            <DD isEmpty={!isValidDefinedDate(startDateMemo)}>{toZonedDateStringOrUndefined(startDateMemo)}</DD>
          ) : (
            <FormField<FormValues, FormPath>
              name="duration.start"
              render={({ field: { value, ...field } }) => (
                <DatepickerV2
                  value={value ? dayjs(value) : undefined}
                  {...field}
                  onChange={(date) => handleStartDateChange(date ?? null, field.onChange)}
                  className="w-full"
                />
              )}
            />
          )}
        </GridCol>
        <GridCol span={1}>
          <DT>{tGlobal('agreement.durationEnd')}</DT>
        </GridCol>
        <GridCol span={2} loading={isFetching}>
          {!editing ? (
            <DD isEmpty={!isValidDefinedDate(endDateMemo)}>{toZonedDateStringOrUndefined(endDateMemo)}</DD>
          ) : (
            <FormField<FormValues, FormPath>
              name="duration.end"
              render={({ field: { value, ...field } }) => (
                <DatepickerV2
                  value={value ? dayjs(value) : dayjs().add(5, 'year')}
                  {...field}
                  onChange={(date) => handleEndDateChange(date ?? null, field.onChange)}
                  className="w-full"
                />
              )}
            />
          )}
        </GridCol>
        <GridCol span={1}>
          <DT>{tGlobal('agreement.optionLength')}</DT>
        </GridCol>
        <GridCol span={2} loading={isFetching}>
          {!editing ? (
            <DD isEmpty={!isDefinedNumber(optionLength?.count)}>
              {optionLength?.count} {optionLength?.type === 'Month' ? tCommon('month') : tCommon('year')}
            </DD>
          ) : (
            <GridRow cols={2} spacingX={1}>
              <GridCol span={1}>
                <FormField<FormValues, FormPath>
                  name={'optionLength.count' as FormPath}
                  render={({ field }) => <Input type="number" size="medium" placeholder="0" {...field} />}
                />
              </GridCol>
              <GridCol span={1}>
                <FormField<FormValues, FormPath>
                  name={'optionLength.type' as FormPath}
                  render={({ field }) => (
                    <Select size="medium" {...field}>
                      <option value="Month">{tCommon('month')}</option>
                      <option value="Year">{tCommon('year')}</option>
                    </Select>
                  )}
                />
              </GridCol>
            </GridRow>
          )}
        </GridCol>
        <GridCol span={1}>
          <DT>{tGlobal('agreement.notificationDeadline')}</DT>
        </GridCol>
        <GridCol span={2} loading={isFetching}>
          {!editing ? (
            <DD isEmpty={!isDefinedNumber(notificationDeadlineMonths)}>
              {notificationDeadlineMonths} {tGlobal('agreement.monthsBeforeEnd')}
            </DD>
          ) : (
            <GridRow cols={2} spacingX={1}>
              <GridCol span={1}>
                <FormField<FormValues, FormPath>
                  name="notificationDeadlineMonths"
                  render={({ field }) => (
                    <Input
                      type="number"
                      size="medium"
                      placeholder="0"
                      {...field}
                    />
                  )}
                />
              </GridCol>
              <GridCol span={1}>
                <DD className="flex items-center h-full pl-2">{tGlobal('agreement.monthsBeforeEnd')}</DD>
              </GridCol>
            </GridRow>
          )}
        </GridCol>
        <GridCol span={1}>
          <DT>{tGlobal('agreement.terminationNoticePeriod')}</DT>
        </GridCol>
        <GridCol span={2} loading={isFetching}>
          {!editing ? (
            <DD isEmpty={!isDefinedNumber(terminationNoticePeriodMonths)}>
              {terminationNoticePeriodMonths} {tCommon('months')}
            </DD>
          ) : (
            <GridRow cols={2} spacingX={1}>
              <GridCol span={1}>
                <FormField<FormValues, FormPath>
                  name="terminationNoticePeriodMonths"
                  render={({ field }) => (
                    <Input
                      type="number"
                      size="medium"
                      placeholder="0"
                      {...field}
                    />
                  )}
                />
              </GridCol>
              <GridCol span={1}>
                <DD className="flex items-center h-full pl-2">{tCommon('months')}</DD>
              </GridCol>
            </GridRow>
          )}
        </GridCol>
      </GridRow>
    </div>
  );
};
